import React, { useEffect, useRef } from 'react';
import { Facebook, Instagram, Youtube } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { getContactConfig } from '@/config/contactConfig';
import WhatsAppIcon from '@/components/homepage/icons/WhatsAppIcon';

const SocialButton: React.FC<{ icon: React.ReactNode; href: string; delay: number }> = ({
  icon, href, delay
}) => {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="w-12 h-12 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full flex items-center justify-center transition-all hover:bg-white/10 animate-on-scroll"
      style={{ animationDelay: `${delay}ms` }}
    >
      {icon}
    </a>
  );
};

const ContactItem: React.FC<{ language: string; countryCode: string; phoneNumber: string; delay: number }> = ({
  language, countryCode, phoneNumber, delay
}) => {
  const whatsappLink = `https://wa.me/${phoneNumber.replace(/[^0-9]/g, '')}`;

  return (
    <div
      className="flex items-center p-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg animate-on-scroll"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="w-8 h-8 flex items-center justify-center mr-3">
        <span className="emoji-flag-language">{countryCode}</span>
      </div>
      <span className="text-lg text-white flex-grow">{language}</span>
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center w-10 h-10 bg-purple-500/20 hover:bg-purple-500/30 rounded-full transition-colors"
      >
        <WhatsAppIcon className="h-6 w-6 text-white" />
      </a>
    </div>
  );
};

// Custom TikTok icon component
const TikTokIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6 text-white"
    viewBox="0 0 24 24"
    fill="currentColor"
    strokeWidth="0"
  >
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64c.16 0 .29 0 .45.05V9.32a6.37 6.37 0 00-3.45 11.7 6.36 6.36 0 009.27-5.62l-.06-9.53a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-1.23l.13-.07z" />
  </svg>
);

// Custom Telegram icon component
const TelegramIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6 text-white"
    viewBox="0 0 24 24"
    fill="currentColor"
    strokeWidth="0"
  >
    <path d="M20.665 3.717l-17.73 6.837c-1.21.486-1.203 1.161-.222 1.462l4.552 1.42 10.532-6.645c.498-.303.953-.14.579.192l-8.533 7.701h-.002l.002.001-.314 4.692c.46 0 .663-.211.921-.46l2.211-2.15 4.599 3.397c.848.467 1.457.227 1.668-.785l3.019-14.228c.309-1.239-.473-1.8-1.282-1.434z" />
  </svg>
);

const ConnectSection: React.FC = () => {
  const { t } = useLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);
  const contactConfig = getContactConfig();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const animateElements = document.querySelectorAll('.animate-on-scroll');
    animateElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      animateElements.forEach((element) => {
        observer.unobserve(element);
      });
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-20 relative bg-gradient-to-b from-indigo-950/30 to-gray-900">
      {/* Background elements */}
      <div className="absolute inset-0 bg-cover bg-center opacity-5"></div>
      <div className="absolute top-0 right-1/3 w-80 h-80 rounded-full bg-indigo-500/5 blur-3xl"></div>
      <div className="absolute bottom-0 left-1/3 w-64 h-64 rounded-full bg-purple-500/10 blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold mb-2 text-white animate-on-scroll">
            {t('connect.header')}
          </h2>

          <p className="text-3xl md:text-3x1 text-purple-400 mb-8 animate-on-scroll">
            {t('connect.subtitle')}
          </p>

          <div className="flex flex-wrap justify-center gap-6 mb-16 animate-on-scroll">
            <SocialButton
              icon={<Instagram className="h-6 w-6 text-white" />}
              href={t('social.instagram')}
              delay={0}
            />
            <SocialButton
              icon={<TikTokIcon />}
              href={t('social.tiktok')}
              delay={0}
            />
            <SocialButton
              icon={<Youtube className="h-6 w-6 text-white" />}
              href={t('social.youtube')}
              delay={0}
            />
            <SocialButton
              icon={<Facebook className="h-6 w-6 text-white" />}
              href={t('social.facebook')}
              delay={0}
            />
            <SocialButton
              icon={<TelegramIcon />}
              href={t('social.telegram')}
              delay={0}
            />
          </div>

          <div className="max-w-4xl mx-auto">
            <h3 className="text-xl font-semibold mb-6 text-white animate-on-scroll">
              {t('connect.whatsapp')}
            </h3>

            <div className="grid justify-center grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-3">
              {contactConfig.contacts.map((contact, idx) => (
                <div key={idx} className={contact.visible ? '' : 'hidden'}>
                  <ContactItem
                    language={t(contact.connectLanguageKey)}
                    countryCode={contact.countryCode}
                    phoneNumber={contact.phoneNumber}
                    delay={0}
                  />
                </div>
              ))}
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default ConnectSection;
